import { StateMethods, useState } from '@hookstate/core';
import { useContext, useState as classicUseState } from 'react';

import CloseImage from '@assets/svg/fat-close.svg';
import PlusImage from '@assets/svg/plus-gray.svg';

import * as yup from 'yup';
import { Icon as BlueIcon } from '@blueprintjs/core';
import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';
import { DeleteDialog, ITooltip, Label as ILabel, Toggle } from '@components';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { FieldTypeOptions, ICustomField } from '@models/CustomField.model';
import { NavigationContext } from '@layout/navigation/navigation';
import {
  createCustomField,
  updateCustomField,
} from '@services/queries/customField';
import { yupResolver } from '@hookform/resolvers/yup';
import { useToast } from '@hooks/index';
import { ScrollArea } from '@mantine/core';
import {
  Error,
  Close,
  Container,
  Header,
  Input,
  Label,
  Overlay,
  Title,
  ButtonClick,
  ButtonPlus,
  Confirm,
  GridDiv,
  Line,
  RowDiv,
  ShortClose,
  Textarea,
  YesNo,
  Plus,
} from './CreateCustomField.styles';
import {
  ImageClick,
  TextClick,
} from '../CustomFieldDetails/CustomFieldDetails.styles';

interface IFormData {
  name: string;
  description?: string;
  type: string;
  defaultValue?: string;
  showOnOnboard?: boolean;
  isUnique?: boolean;
}

interface ICreateCustomField {
  open: StateMethods<boolean>;
  data: ICustomField[] | undefined;
}

const options = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select' },
  { value: 'url', label: 'Url' },
  { value: 'contact', label: 'Contact' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'description', label: 'Description' },
];

const CreateCustomField = ({ open, data }: ICreateCustomField) => {
  const portalDiv = document.getElementById('portal');
  const queryClient = useQueryClient();
  const [isUnique, setIsUnique] = classicUseState(false);
  const [isSearchable, setIsSearchable] = classicUseState(false);
  const [type, setType] = classicUseState('');
  const [name, setName] = classicUseState('');
  const [selectValues, setSelectValues] = classicUseState<string[]>([]);
  const [haveDefaultValue, setHaveDefaultValue] = classicUseState(false);
  const [showOnOnboard, setShowOnOnboard] = classicUseState(false);
  const [showPlus, setShowPlus] = classicUseState(true);
  const [newSelect, setNewSelect] = classicUseState('');
  const openDeleteDialog = useState<boolean>(false);

  const { studioId } = useContext(NavigationContext);

  const filterSearchableField = data?.filter(e => e.isSearchable);

  const filterSearchableFiltered = filterSearchableField?.find(
    e => e.isSearchable,
  );

  const toggleDefaultStatus = () => {
    if (filterSearchableField && !isSearchable) {
      openDeleteDialog.set(true);
    }
    return setIsSearchable(!isSearchable);
  };

  const { toast } = useToast();

  const schema = yup.object().shape({
    name: yup.string().required(),
    type: yup.string().required(),
    description: yup.string(),
    defaultValue: yup.string(),
    showOnOnboard: yup.boolean(),
    isUnique: yup.boolean(),
  });
  const {
    register,
    handleSubmit,
    formState: { isValid },
    control,
    reset,
    clearErrors,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const updateCustomFieldMutation = useMutation(updateCustomField, {
    onSuccess: () => {
      queryClient.invalidateQueries(['studios', studioId, 'customField']);
      setSelectValues([]);
      setShowPlus(true);
      setHaveDefaultValue(false);
      setType('');
      setIsUnique(false);
      setShowOnOnboard(false);
      setNewSelect('');
      setIsSearchable(false);
      open.set(false);
      reset();
      clearErrors();
    },
  });
  const createCustomFieldMutation = useMutation(createCustomField, {
    onSuccess: () => {
      if (filterSearchableField && isSearchable) {
        updateCustomFieldMutation.mutate({
          customFieldId: filterSearchableFiltered?.id,
          data: {
            isSearchable: false,
          },
        });
      }
      queryClient.invalidateQueries(['studios', studioId, 'customField']);
      setSelectValues([]);
      setShowPlus(true);
      setHaveDefaultValue(false);
      setType('');
      setIsUnique(false);
      setShowOnOnboard(false);
      setNewSelect('');
      setIsSearchable(false);
      open.set(false);
      reset();
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });
  const onSubmit = (dataInput: IFormData) => {
    if (!createCustomFieldMutation.isLoading) {
      createCustomFieldMutation.mutate({
        studioId,
        data: {
          name: dataInput.name,
          type: dataInput.type as FieldTypeOptions,
          description: dataInput.description,
          defaultValue: dataInput.defaultValue,
          showOnOnboard,
          selectValues,
          isUnique,
          isSearchable,
        },
      });
    }
  };
  const getNameIndex =
    data &&
    data.findIndex(
      item => item.name.toLocaleLowerCase() === name.toLocaleLowerCase(),
    );
  const isErrorName = getNameIndex !== undefined && getNameIndex > -1;
  // Data
  const defaultTip =
    'When set to “Yes”, the value provided will be added by default when adding a new asset.';
  const onboardTip =
    'If set to “Yes” this field will be shown after an asset is added in the “onboard” step. When off, this field will only be editable during mass edit or in the asset page.';
  const uniqueFieldTip =
    'If set to "Yes", this field will need a unique value that can only be used once. Useful for Asset Tags/IDs for instance.';
  return open.get() && portalDiv ? (
    <Overlay>
      <Container>
        <form onSubmit={handleSubmit(onSubmit)} className="form-body">
          <Header>
            <Title className="headTitle">Add Custom Field</Title>
            <Close onClick={() => open.set(false)}>
              <BlueIcon icon="cross" color="#8F95B2" />
            </Close>
          </Header>
          <ScrollArea classNames={{ viewport: 'form-body_inputs' }}>
            <Label className="no-margin-top">Name</Label>
            <Input
              placeholder="Choose a Custom Field Name"
              autoFocus
              {...register('name')}
              isError={isErrorName}
              onChange={(e: any) => {
                setName(e.target.value);
              }}
            />
            {isErrorName && (
              <Error>There is already a custom field with this name</Error>
            )}
            <Label className="optional">Description (Optional)</Label>
            <Textarea placeholder="" {...register('description')} />
            <Label>Type</Label>
            <Controller
              control={control}
              name="type"
              render={({ field: { onChange, value } }) => (
                <CustomPicker
                  options={options}
                  value={options.find(c => c.value === value)}
                  onChange={(val: any) => {
                    onChange(val.value);
                    setType(val.value);
                  }}
                  placeholder="Select a field type"
                />
              )}
            />
            {type === 'select' && (
              <>
                <Label>Select Values</Label>
                <GridDiv>
                  {selectValues.map(item => {
                    return (
                      <ButtonClick>
                        <TextClick>{item}</TextClick>
                        <ImageClick>
                          <ShortClose
                            src={CloseImage}
                            alt="close"
                            onClick={async () => {
                              setSelectValues(
                                selectValues.filter(value => value !== item),
                              );
                            }}
                          />
                        </ImageClick>
                      </ButtonClick>
                    );
                  })}
                  {showPlus ? (
                    <ButtonPlus>
                      <Plus
                        src={PlusImage}
                        alt="plus"
                        onClick={async () => {
                          setShowPlus(false);
                        }}
                      />
                    </ButtonPlus>
                  ) : (
                    <Input
                      placeholder="Add a new select value"
                      onChange={(e: any) => {
                        setNewSelect(e.target.value);
                      }}
                      onBlur={() => {
                        if (newSelect.length > 0) {
                          setSelectValues([...selectValues, newSelect]);
                          setNewSelect('');
                          setShowPlus(true);
                        }
                      }}
                      onKeyDown={(event: any) => {
                        if (event.key === 'Enter' && newSelect.length > 0) {
                          setSelectValues([...selectValues, newSelect]);
                          setNewSelect('');
                          setShowPlus(true);
                        }
                      }}
                    />
                  )}
                </GridDiv>
              </>
            )}
            <ILabel data-for="custom-fields-tip" tip={defaultTip}>
              Default Value
            </ILabel>
            <RowDiv>
              <Toggle
                selected={haveDefaultValue}
                toggleSelected={() => setHaveDefaultValue(!haveDefaultValue)}
              />
              <YesNo>{haveDefaultValue ? 'Yes' : 'No'}</YesNo>
              <Input
                disabled={!haveDefaultValue}
                placeholder="Define the default Value"
                {...register('defaultValue')}
              />
            </RowDiv>
            <ILabel data-for="custom-fields-tip" tip={onboardTip}>
              Show on Onboard
            </ILabel>
            <RowDiv>
              <Toggle
                selected={showOnOnboard}
                toggleSelected={() => setShowOnOnboard(!showOnOnboard)}
              />
              <YesNo>{showOnOnboard ? 'Yes' : 'No'}</YesNo>
            </RowDiv>
            <ILabel data-for="custom-fields-tip" tip={uniqueFieldTip}>
              Unique Field
            </ILabel>
            <RowDiv>
              <Toggle
                selected={isUnique}
                toggleSelected={() => setIsUnique(!isUnique)}
              />
              <YesNo>{isUnique ? 'Yes' : 'No'}</YesNo>
            </RowDiv>
            <ILabel>Is Searchable</ILabel>
            <RowDiv>
              <Toggle
                selected={isSearchable}
                toggleSelected={() => toggleDefaultStatus()}
              />
              <YesNo>{isSearchable ? 'Yes' : 'No'}</YesNo>
            </RowDiv>
          </ScrollArea>
          <Line className="no-margin-padding">
            <Confirm
              disabled={
                !isValid ||
                createCustomFieldMutation.isLoading ||
                (type === 'select' && selectValues.length === 0) ||
                isErrorName
              }
            >
              Add
            </Confirm>
          </Line>
          <ITooltip id="custom-fields-tip" />
        </form>
      </Container>

      {openDeleteDialog && (
        <DeleteDialog
          open={openDeleteDialog}
          onDelete={() => {
            setIsSearchable(true);
            openDeleteDialog.set(false);
          }}
          backgroundColor="#FF9900"
          buttonText="Replace Searchable Field ?"
          text="There is already one custom field set as searchable"
          onCancel={() => setIsSearchable(false)}
        />
      )}
    </Overlay>
  ) : null;
};

export { CreateCustomField };
